import { useCallback, useMemo } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { SEVERITY_RANK, changeOf, lostOf } from "./drops";

export const DEFAULT_FILTERS = { changeWindow: null, severity: "all", type: "all", cause: "all", query: "" };
const DEFAULT_SORT = { key: "severity", dir: "desc" };
const FILTER_PARAMS = ["changes", "severity", "type", "cause", "q"];

const windowOf = (param) => {
    if (!param) return null;
    const [start, end] = param.split("_");
    return { start, end: end ?? start };
};
const paramOfWindow = (window) => (window.start === window.end ? window.start : `${window.start}_${window.end}`);

function filtersOf(params) {
    return {
        changeWindow: windowOf(params.get("changes")),
        severity: params.get("severity") ?? DEFAULT_FILTERS.severity,
        type: params.get("type") ?? DEFAULT_FILTERS.type,
        cause: params.get("cause") ?? DEFAULT_FILTERS.cause,
        query: params.get("q") ?? DEFAULT_FILTERS.query,
    };
}

/** Writes filters into params, leaving a filter at its default out of the URL. */
function setFilterParams(params, filters) {
    const values = {
        changes: filters.changeWindow ? paramOfWindow(filters.changeWindow) : null,
        severity: filters.severity,
        type: filters.type,
        cause: filters.cause,
        q: filters.query,
    };
    const defaults = { changes: null, severity: DEFAULT_FILTERS.severity, type: DEFAULT_FILTERS.type, cause: DEFAULT_FILTERS.cause, q: DEFAULT_FILTERS.query };
    FILTER_PARAMS.forEach((key) => {
        if (values[key] === undefined) return;
        if (values[key] === defaults[key] || values[key] === null) params.delete(key);
        else params.set(key, values[key]);
    });
}

function matches(drop, filters, ignoreSeverity = false) {
    const window = filters.changeWindow;
    if (window && !drop.pageChanges.some((change) => change.date >= window.start && change.date <= window.end)) return false;
    if (!ignoreSeverity && filters.severity !== "all" && drop.severity !== filters.severity) return false;
    if (filters.type !== "all" && drop.entityType !== filters.type) return false;
    if (filters.cause !== "all" && drop.cause !== filters.cause) return false;
    const query = filters.query.trim().toLowerCase();
    return !query || `${drop.entity} ${drop.summary} ${drop.cause}`.toLowerCase().includes(query);
}

function compare(a, b, key, measure) {
    if (key === "severity") return SEVERITY_RANK[a.severity] - SEVERITY_RANK[b.severity] || lostOf(a, measure) - lostOf(b, measure);
    if (key === "lost") return lostOf(a, measure) - lostOf(b, measure);
    if (key === "change") return changeOf(b, measure) - changeOf(a, measure);
    return a.detectedAt.localeCompare(b.detectedAt);
}

/**
 * The drops list's filters, sort and selected drop, all in the URL (`?changes=`, `?severity=`,
 * `?type=`, `?cause=`, `?q=`, `?sort=`, `?dir=`, `?drop=`).
 */
export function useDrops(initial, measure) {
    const location = useLocation();
    const history = useHistory();
    const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
    const items = initial;
    const filters = useMemo(() => filtersOf(params), [params]);
    const sort = { key: params.get("sort") ?? DEFAULT_SORT.key, dir: params.get("dir") ?? DEFAULT_SORT.dir };
    const selectedId = params.get("drop");

    const navigate = useCallback((change, replace = false) => {
        const next = new URLSearchParams(location.search);
        change(next);
        const target = { pathname: location.pathname, search: `?${next.toString()}` };
        if (replace) history.replace(target); else history.push(target);
    }, [history, location.pathname, location.search]);

    const filtered = useMemo(() => items
        .filter((drop) => matches(drop, filters))
        .sort((a, b) => (sort.dir === "desc" ? -1 : 1) * compare(a, b, sort.key, measure)), [items, filters, sort.key, sort.dir, measure]);

    const severityCounts = useMemo(() => {
        const base = items.filter((drop) => matches(drop, filters, true));
        return {
            all: base.length,
            critical: base.filter((drop) => drop.severity === "critical").length,
            high: base.filter((drop) => drop.severity === "high").length,
            medium: base.filter((drop) => drop.severity === "medium").length,
        };
    }, [items, filters]);

    const hasActiveFilters = FILTER_PARAMS.some((key) => params.has(key));

    // The search box replaces rather than pushes: one history entry per keystroke would make Back unusable.
    const updateFilter = useCallback((key, value) => navigate((next) => setFilterParams(next, { [key]: value }), key === "query"), [navigate]);
    const applyFilters = useCallback((filtersToApply) => navigate((next) => {
        FILTER_PARAMS.forEach((key) => next.delete(key));
        setFilterParams(next, filtersToApply);
    }), [navigate]);
    const resetFilters = useCallback(() => navigate((next) => FILTER_PARAMS.forEach((key) => next.delete(key))), [navigate]);

    const toggleSort = useCallback((key) => navigate((next) => {
        const dir = sort.key === key && sort.dir === "desc" ? "asc" : "desc";
        if (key === DEFAULT_SORT.key) next.delete("sort"); else next.set("sort", key);
        if (dir === DEFAULT_SORT.dir) next.delete("dir"); else next.set("dir", dir);
    }), [navigate, sort.key, sort.dir]);

    const setSelectedId = useCallback((id) => navigate((next) => (id ? next.set("drop", id) : next.delete("drop"))), [navigate]);

    const selected = items.find((drop) => drop.id === selectedId) ?? null;
    const selectedIndex = filtered.findIndex((drop) => drop.id === selectedId);
    const selectRelative = useCallback((step) => {
        const next = filtered[selectedIndex + step];
        if (next) navigate((params) => params.set("drop", next.id), true);
    }, [filtered, selectedIndex, navigate]);

    return {
        items, filtered, filters, sort, severityCounts, hasActiveFilters, selected, selectedId,
        hasPrev: selectedIndex > 0,
        hasNext: selectedIndex >= 0 && selectedIndex < filtered.length - 1,
        updateFilter, applyFilters, resetFilters, toggleSort, setSelectedId, selectRelative,
    };
}
